import { useId, type ComponentProps } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { parseMoney, type MoneyKind, type ParsedMoney } from "@shared/money";

type MoneyInputProps = Omit<ComponentProps<typeof Input>, "value" | "defaultValue" | "onChange"> & {
  kind: MoneyKind;
  /** The raw text in the box. Seed it with moneyInputValue(stored). */
  value: string;
  onValueChange: (raw: string) => void;
  wrapperClassName?: string;
};

/**
 * A text box for a dollar amount that says, as you type, exactly what will be
 * saved ("Saves as $1,500,000"), and flags an amount that doesn't fit the
 * field ("$1K looks off for a buyer's EBITDA target. Did you mean $1M?") with
 * a one-click fix. The rules live in shared/money.ts, the same ones the server
 * enforces, so the warning here and a 400 there can never disagree.
 *
 * On submit, read the amount with `parseMoney(value, kind)`: send `.dollars`
 * when status is "ok", null when "empty", and don't submit otherwise.
 */
export function MoneyInput({ kind, value, onValueChange, wrapperClassName, className, ...rest }: MoneyInputProps) {
  const hintId = useId();
  const parsed = parseMoney(value, kind);
  const flagged = parsed.status === "invalid" || parsed.status === "implausible";
  return (
    <div className={wrapperClassName}>
      <Input
        {...rest}
        inputMode="decimal"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        aria-invalid={flagged || undefined}
        aria-describedby={parsed.status === "empty" ? undefined : hintId}
        className={cn(flagged && "border-amber-500 focus-visible:ring-amber-500", className)}
      />
      <MoneyHint id={hintId} parsed={parsed} onUse={onValueChange} />
    </div>
  );
}

function MoneyHint({ id, parsed, onUse }: { id: string; parsed: ParsedMoney; onUse: (raw: string) => void }) {
  if (parsed.status === "empty") return null;
  if (parsed.status === "ok") {
    return (
      <p id={id} className="mt-1 text-xs text-muted-foreground">
        Saves as {parsed.full}
      </p>
    );
  }
  return (
    <p id={id} role="alert" className="mt-1 text-xs text-amber-700 dark:text-amber-400">
      {parsed.message}
      {parsed.suggestion && (
        <>
          {" "}
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onClick={() => onUse(parsed.suggestion!.display)}
          >
            Use {parsed.suggestion.display}
          </button>
        </>
      )}
    </p>
  );
}
